import type { AllocationStrategy } from "~/store/allocationSlice";

import { AllocationItem, AllocationLocation } from "~fulfill/orders/columnDefs/allocationModalColDef";

export interface AutoFillGuardInput {
	excludedGradesUnavailable?: boolean;
	priorityGrades: string[];
	priorityLocations: string[];
	selectedLocation: string;
	strategy: AllocationStrategy;
}

export interface AutoFillGuardResult {
	disabled: boolean;
	disabledTooltipKey?: string;
}

interface ProportionalShare {
	fraction: number;
	location: AllocationLocation;
	wholeUnits: number;
}

export const wholeEachesAvailable = (available: number): number => Math.floor(Math.max(0, available));

export const allocationCeiling = (location: AllocationLocation, allowOverallocation: boolean): number => {
	if (location.isCustomerShipTo) return location.savedAllocation;
	return allowOverallocation ? Number.POSITIVE_INFINITY : wholeEachesAvailable(location.baseAvailable);
};

export const withAvailabilityAfterAllocation = (location: AllocationLocation): AllocationLocation => ({
	...location,
	available: location.baseAvailable - location.allocated
});

const mergeFilledLocation =
	(filled: AllocationLocation[]) =>
	(original: AllocationLocation): AllocationLocation => {
		const match = filled.find((f) => f.id === original.id && f.grade === original.grade);
		return match ? withAvailabilityAfterAllocation(match) : original;
	};

export const resetLocationsForRefill = (locations: AllocationLocation[]): AllocationLocation[] =>
	locations.map((location) => ({ ...location, allocated: 0, available: location.baseAvailable }));

export const isPickableGrade = (
	grade: number,
	priorityGrades: number[],
	isExcludedGrade: (grade: number) => boolean
): boolean => !isExcludedGrade(grade) || priorityGrades.includes(grade);

export const sumLocationAvailability = (locations: AllocationLocation[]): number =>
	locations.reduce((sum, location) => (location.isTransfer ? sum : sum + location.available), 0);

export const collapseFilledLocationsIntoItem = (item: AllocationItem, filled: AllocationLocation[]): AllocationItem => {
	const locations = (item.locations ?? []).map(mergeFilledLocation(filled));

	return {
		...item,
		allocated: filled.reduce((sum, location) => sum + location.allocated, 0),
		available: sumLocationAvailability(locations),
		locations
	};
};

export const fillSingleLocation = (
	locations: AllocationLocation[],
	target: number,
	selectedLocationId: string
): AllocationLocation[] => {
	let remaining = target;
	return locations.map((location) => {
		if (location.id !== selectedLocationId) return { ...location, allocated: 0 };
		const canAllocate = Math.min(remaining, wholeEachesAvailable(location.available));
		remaining -= canAllocate;
		return { ...location, allocated: canAllocate };
	});
};

const toProportionalShare = (
	location: AllocationLocation,
	fillTarget: number,
	totalHeadroom: number
): ProportionalShare => {
	const exactShare = (fillTarget * wholeEachesAvailable(location.available)) / totalHeadroom;
	const wholeUnits = Math.floor(exactShare);
	return { fraction: exactShare - wholeUnits, location, wholeUnits };
};

const findBestRemainderCandidateIndex = (shares: ProportionalShare[]): number =>
	shares.reduce((bestIndex, share, index) => {
		const hasHeadroom = share.wholeUnits < wholeEachesAvailable(share.location.available);
		const isBetter = bestIndex === -1 || share.fraction > shares[bestIndex].fraction;
		return hasHeadroom && isBetter ? index : bestIndex;
	}, -1);

const addOneUnitToBestCandidate = (shares: ProportionalShare[]): ProportionalShare[] => {
	const bestIndex = findBestRemainderCandidateIndex(shares);
	return bestIndex === -1
		? shares
		: shares.map((share, index) => (index === bestIndex ? { ...share, wholeUnits: share.wholeUnits + 1 } : share));
};

const distributeRemainderUnits = (shares: ProportionalShare[], unitsToDistribute: number): ProportionalShare[] =>
	Array.from({ length: Math.max(0, unitsToDistribute) }).reduce<ProportionalShare[]>(
		(current) => addOneUnitToBestCandidate(current),
		shares
	);

export const distributeBalanced = (locations: AllocationLocation[], target: number): AllocationLocation[] => {
	const totalHeadroom = locations.reduce((sum, location) => sum + wholeEachesAvailable(location.available), 0);
	if (totalHeadroom <= 0) return locations.map((location) => ({ ...location, allocated: 0 }));

	const fillTarget = Math.min(Math.max(0, target), totalHeadroom);
	const initialShares = locations.map((location) => toProportionalShare(location, fillTarget, totalHeadroom));
	const remainderUnits = fillTarget - initialShares.reduce((sum, share) => sum + share.wholeUnits, 0);

	return distributeRemainderUnits(initialShares, remainderUnits).map((share) => ({
		...share.location,
		allocated: share.wholeUnits
	}));
};

const isSingleLocationMissingSelection = ({ selectedLocation, strategy }: AutoFillGuardInput): boolean =>
	strategy === "singleLocation" && !selectedLocation;

const isPriorityMissingRanking = ({ priorityGrades, priorityLocations, strategy }: AutoFillGuardInput): boolean =>
	strategy === "priority" && priorityLocations.length === 0 && priorityGrades.length === 0;

export const resolveAutoFillGuard = (input: AutoFillGuardInput): AutoFillGuardResult => {
	if (input.excludedGradesUnavailable) {
		return { disabled: true, disabledTooltipKey: "orders.allocation.autoFill.excludedGradesUnavailable" };
	}
	if (isSingleLocationMissingSelection(input)) {
		return { disabled: true, disabledTooltipKey: "orders.allocation.autoFill.singleLocationRequired" };
	}
	if (isPriorityMissingRanking(input)) {
		return { disabled: true, disabledTooltipKey: "orders.allocation.autoFill.priorityRankingRequired" };
	}
	return { disabled: false };
};

const allocationStrategyFill = {
	allocationCeiling,
	collapseFilledLocationsIntoItem,
	distributeBalanced,
	fillSingleLocation,
	resetLocationsForRefill,
	resolveAutoFillGuard,
	sumLocationAvailability,
	wholeEachesAvailable,
	withAvailabilityAfterAllocation
};

export default allocationStrategyFill;
